import React, { useCallback, useEffect, useState } from 'react'
import { Box, IconButton } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import { useCampaignModalSkeletonStyles, G } from '../CampaignModalSkeleton.styles'

export interface NumSpinnerProps {
	value: number | null
	onChange: (v: number | null) => void
	min?: number
	max?: number
	suffix?: string
	disabled?: boolean
	placeholder?: string
	step?: number | 'any'
}

export function NumSpinner({
	value,
	onChange,
	min = 1,
	max = 99,
	suffix = '',
	disabled = false,
	placeholder = '20',
	step = 1,
}: NumSpinnerProps) {
	const classes = useCampaignModalSkeletonStyles()
	const [focused, setFocused] = useState(false)
	const [draft, setDraft] = useState(() =>
		value === null || value === undefined ? '' : String(value),
	)

	useEffect(() => {
		if (!focused) {
			setDraft(value === null || value === undefined ? '' : String(value))
		}
	}, [value, focused])

	const parseDraftValue = useCallback((nextDraft: string) => {
		const t = nextDraft.trim().replace(',', '.')
		if (t === '') {
			return null
		}
		const n = Number(t)
		if (Number.isNaN(n)) {
			return null
		}
		let clamped = Math.max(min, Math.min(max, n))
		if (step !== 'any') {
			clamped = Math.round(clamped)
		}
		return clamped
	}, [max, min, step])

	const commitDraft = useCallback(() => {
		onChange(parseDraftValue(draft))
	}, [draft, onChange, parseDraftValue])

	const handleDec = useCallback(() => {
		if (disabled) {
			return
		}
		const current =
			value === null || value === undefined ? min : value
		const next = current - 1
		let n = step === 'any' ? next : Math.round(next)
		n = Math.max(min, Math.min(max, n))
		onChange(n)
	}, [disabled, max, min, onChange, step, value])

	const handleInc = useCallback(() => {
		if (disabled) {
			return
		}
		const current =
			value === null || value === undefined ? min - 1 : value
		const next = current + 1
		let n = step === 'any' ? next : Math.round(next)
		n = Math.max(min, Math.min(max, n))
		onChange(n)
	}, [disabled, max, min, onChange, step, value])

	const displayEmpty = value === null || value === undefined
	const atMin = !displayEmpty && value <= min
	const atMax = !displayEmpty && value >= max
	const showValueColor = draft !== ''

	return (
		<Box className={classes.numSpinner} style={{ opacity: disabled ? 0.5 : 1 }}>
			<IconButton
				className={classes.numSpinnerBtn}
				onClick={handleDec}
				size="small"
				disabled={disabled || atMin}
			>
				<RemoveIcon fontSize="small" />
			</IconButton>
			<Box className={classes.numSpinnerValue}>
				<input
					type="number"
					className={classes.numSpinnerInput}
					disabled={disabled}
					min={min}
					max={max}
					step={step === 'any' ? 'any' : step}
					value={draft}
					placeholder={placeholder}
					onFocus={() => setFocused(true)}
					onBlur={() => {
						setFocused(false)
						commitDraft()
					}}
					onChange={(e) => {
						const nextDraft = e.target.value
						setDraft(nextDraft)
						onChange(parseDraftValue(nextDraft))
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							;(e.target as HTMLInputElement).blur()
						}
					}}
					style={{
						color: showValueColor ? G : '#9e9e9e',
					}}
				/>
				{suffix ? (
					<span
						style={{
							fontSize: 14,
							fontWeight: 600,
							color: showValueColor || !displayEmpty ? G : '#bdbdbd',
							flexShrink: 0,
						}}
					>
						{suffix}
					</span>
				) : null}
			</Box>
			<IconButton
				className={classes.numSpinnerBtn}
				onClick={handleInc}
				size="small"
				disabled={disabled || atMax}
			>
				<AddIcon fontSize="small" />
			</IconButton>
		</Box>
	)
}
